<template>
  <div class="language-switcher">
    <!-- compact: a single toggle, for the top bar -->
    <v-btn
      v-if="compact"
      depressed
      small
      outlined
      :loading="saving"
      :color="color"
      :aria-label="$t('language.language')"
      class="text-capitalize"
      @click="toggle"
    >
      <v-icon small class="me-1">mdi-translate</v-icon>
      {{ otherOption.nativeLabel }}
    </v-btn>

    <!-- full: an explicit choice, for the profile drawer / settings -->
    <v-select
      v-else
      :value="currentLocale"
      :items="options"
      item-text="nativeLabel"
      item-value="locale"
      :label="$t('language.language')"
      :loading="saving"
      :disabled="saving"
      prepend-inner-icon="mdi-translate"
      hide-details
      outlined
      dense
      @change="select"
    ></v-select>

    <v-snackbar v-model="snackbar" :color="snackColor" timeout="4000" right top>
      {{ snackText }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  LANGUAGE_OPTIONS,
  normalizeLocale,
  setUserLanguage,
  consumeSaveWarning,
} from "@/utils/language-service";

export default {
  name: "LanguageSwitcher",
  props: {
    // single-button form, for tight spaces like the app bar
    compact: { type: Boolean, default: false },
    color: { type: String, default: "primary" },
  },
  data() {
    return {
      options: LANGUAGE_OPTIONS,
      saving: false,
      snackbar: false,
      snackText: "",
      snackColor: "success",
    };
  },
  computed: {
    currentLocale() {
      return normalizeLocale(this.$i18n.locale);
    },
    otherOption() {
      return (
        this.options.find(o => o.locale !== this.currentLocale) || this.options[0]
      );
    },
  },
  mounted() {
    // The switch reloads the page, so a failed save has to report itself on the
    // way back up rather than at the point of failure.
    if (consumeSaveWarning()) {
      this.snackText = this.$t("language.failedToSave");
      this.snackColor = "warning";
      this.snackbar = true;
    }
  },
  methods: {
    toggle() {
      this.select(this.otherOption.locale);
    },
    select(locale) {
      if (!locale || locale === this.currentLocale || this.saving) return;
      this.saving = true;
      // Persists to the user record and then reloads, so this component is
      // torn down before the promise settles in the happy path.
      setUserLanguage(locale);
    },
  },
};
</script>

<style scoped>
.language-switcher {
  min-width: 0;
}
</style>
